class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {number | null} */
  #queuedSync = null;

  /**
   * Lightweight listener for same-page product option changes.
   * @type {(event: Event) => void}
   */
  #productOptionChangeHandler = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const variantPicker = target.closest('variant-picker[data-template-product-match="true"]');
    if (
      !variantPicker ||
      variantPicker.closest('product-card') ||
      variantPicker.closest('quick-add-dialog')
    ) {
      return;
    }

    const swatchLabel = target.closest('label[data-glaze]');
    if (swatchLabel instanceof HTMLElement && variantPicker.contains(swatchLabel)) {
      this.#queueProductCardSync(swatchLabel.dataset.glaze || null, swatchLabel.dataset.clay || null);
      return;
    }

    if (
      target instanceof HTMLSelectElement &&
      (target.name === 'options[Glaze]' || target.name === 'options[Clay]')
    ) {
      this.#queueProductCardSync(null, null);
    }
  };

  connectedCallback() {
    // Check if recommendations are already loaded server-side
    // If so, don't try to load them via JavaScript
    const recommendationsPerformed = this.dataset.recommendationsPerformed === 'true';
    
    if (!recommendationsPerformed) {
      // Only observe and load if recommendations haven't been loaded server-side
      this.#intersectionObserver.observe(this);
      this.#mutationObserver.observe(this, { attributes: true });
    }
    
    document.addEventListener('click', this.#productOptionChangeHandler, true);
    document.addEventListener('change', this.#productOptionChangeHandler, true);
    
    // Also check for initial glaze selection from URL or existing selection
    this.#checkInitialGlaze();
  }

  #queueProductCardSync(selectedGlaze = null, selectedClay = null) {
    if (this.#queuedSync) window.clearTimeout(this.#queuedSync);

    this.#queuedSync = window.setTimeout(() => {
      this.#queuedSync = null;

      let glaze = selectedGlaze;
      let clay = selectedClay;

      if (!glaze) {
        const mainVariantPicker = document.querySelector('variant-picker[data-template-product-match="true"]:not(product-card variant-picker):not(quick-add-dialog variant-picker)');
        if (!mainVariantPicker) return;

        glaze = this.#getSelectedGlaze(mainVariantPicker);
        clay = clay || this.#getSelectedClay(mainVariantPicker);
      }

      if (glaze) {
        this.dataset.selectedGlaze = glaze;
        if (clay) this.dataset.selectedClay = clay;
        this.#updateProductCardImages(glaze, clay);
      }
    }, 0);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#productOptionChangeHandler, true);
    document.removeEventListener('change', this.#productOptionChangeHandler, true);
    if (this.#queuedSync) window.clearTimeout(this.#queuedSync);
  }

  /**
   * Checks for initial glaze selection from URL parameters or existing variant picker
   */
  #checkInitialGlaze() {
    // Check if there's a variant picker on the main product page
    const mainVariantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (mainVariantPicker) {
      const selectedGlaze = this.#getSelectedGlaze(mainVariantPicker);
      const selectedClay = this.#getSelectedClay(mainVariantPicker);
      if (selectedGlaze) {
        this.dataset.selectedGlaze = selectedGlaze;
        if (selectedClay) this.dataset.selectedClay = selectedClay;
        // Only update if recommendations are already loaded
        if (this.dataset.recommendationsPerformed === 'true') {
          this.#updateProductCardImages(selectedGlaze, selectedClay);
        }
      }
    }
  }

  /**
   * Gets the selected glaze value from the variant picker
   * @param {HTMLElement} variantPicker - The variant picker element
   * @returns {string | null} The selected glaze value, or null if not found
   */
  #getSelectedGlaze(variantPicker) {
    // First, try to find the glaze select element (most reliable)
    const glazeSelect = variantPicker.querySelector('select[name*="glaze" i], select[name*="Glaze"], select[name="options[Glaze]"]');
    if (glazeSelect && glazeSelect.selectedOptions.length > 0) {
      const selectedValue = glazeSelect.selectedOptions[0].value;
      if (selectedValue) return selectedValue;
    }

    // Try to find selected glaze from combined swatch picker
    const combinedSwatchPicker = variantPicker.querySelector('combined-swatch-picker');
    if (combinedSwatchPicker) {
      // Method 1: Find the selected swatch label (it has data-selected="true")
      const selectedLabel = combinedSwatchPicker.querySelector('label[data-selected="true"]');
      if (selectedLabel && selectedLabel.dataset.glaze) {
        return selectedLabel.dataset.glaze;
      }
      
      // Method 2: Find the checked input and get glaze from its parent label
      const selectedInput = combinedSwatchPicker.querySelector('input[type="radio"]:checked');
      if (selectedInput) {
        const label = selectedInput.closest('label[data-glaze]');
        if (label && label.dataset.glaze) {
          return label.dataset.glaze;
        }
      }

      // Method 3: Get from the glaze select that the combined swatch picker updates
      // The combined swatch picker updates hidden selects, so check those
      const form = variantPicker.querySelector('form');
      if (form) {
        const hiddenGlazeSelect = form.querySelector('select[name="options[Glaze]"]');
        if (hiddenGlazeSelect && hiddenGlazeSelect.value) {
          return hiddenGlazeSelect.value;
        }
      }
    }

    return null;
  }

  /**
   * Gets the selected clay value from the variant picker.
   * @param {HTMLElement} variantPicker
   * @returns {string | null}
   */
  #getSelectedClay(variantPicker) {
    const claySelect = variantPicker.querySelector('select[name*="clay" i], select[name*="Clay"], select[name="options[Clay]"]');
    if (claySelect && claySelect.selectedOptions.length > 0) {
      const selectedValue = claySelect.selectedOptions[0].value;
      if (selectedValue) return selectedValue;
    }

    const combinedSwatchPicker = variantPicker.querySelector('combined-swatch-picker');
    if (combinedSwatchPicker) {
      const selectedLabel = combinedSwatchPicker.querySelector('label[data-selected="true"]');
      if (selectedLabel?.dataset.clay) return selectedLabel.dataset.clay;

      const selectedInput = combinedSwatchPicker.querySelector('input[type="radio"]:checked');
      const label = selectedInput?.closest('label[data-clay]');
      if (label?.dataset.clay) return label.dataset.clay;

      const form = variantPicker.querySelector('form');
      const hiddenClaySelect = form?.querySelector('select[name="options[Clay]"]');
      if (hiddenClaySelect?.value) return hiddenClaySelect.value;
    }

    return null;
  }

  /**
   * Updates product card images in recommendations to show the matching variant.
   * @param {string} selectedGlaze - The selected glaze value
   * @param {string | null} [selectedClay] - The selected clay value
   */
  async #updateProductCardImages(selectedGlaze, selectedClay = null) {
    const productCards = this.querySelectorAll('product-card');
    
    for (const productCard of productCards) {
      const productId = productCard.dataset.productId;
      if (!productId) continue;

      const cardGallery = productCard.querySelector('card-gallery, [ref="cardGallery"]');
      if (!cardGallery) continue;

      // Find the variant that matches the selected Clay + Glaze. Matching by
      // glaze alone is ambiguous because several clay bodies can share the
      // same glaze name.
      const matchingVariant = await this.#findVariantByGlaze(productId, selectedGlaze, productCard, selectedClay);
      if (matchingVariant) {
        this.#syncProductCardSwatch(productCard, matchingVariant);
        this.#updateCardGalleryImage(cardGallery, matchingVariant);
      }
    }
  }

  /**
   * Keeps the visible card swatch carousel aligned with the recommendation
   * image forced by the main product's selected glaze.
   *
   * The recommendations section can render duplicate product cards (desktop and
   * mobile carousel variants), so the product-card component scopes radio
   * names per card. Dispatching a change event then lets the card carousel move
   * its active bubble to the same variant that the image is showing.
   * @param {HTMLElement} productCard
   * @param {Object} variant
   * @returns {boolean}
   */
  #syncProductCardSwatch(productCard, variant) {
    const variantId = variant?.id ? String(variant.id) : '';
    const mediaId = variant?.mediaId || variant?.featured_media?.id || variant?.image?.id;
    const mediaIdString = mediaId ? String(mediaId) : '';

    // IMPORTANT: only consider the REAL swatch inputs. The carousel clones the
    // full swatch set (leading + trailing) for the infinite loop, and clones
    // share the same data-variant-id. Clones appear first in DOM order and are
    // disabled. If we dispatched the change on a clone, the carousel's change
    // handler bails out (it ignores clone items) and the active bubble never
    // moves - which is exactly the "image updates but swatch is stuck" bug.
    const inputs = Array.from(
      productCard.querySelectorAll('.combined-swatch-input[data-combined-swatch="true"]')
    ).filter((candidate) => {
      if (!(candidate instanceof HTMLInputElement)) return false;
      if (candidate.disabled) return false;
      return !candidate.closest('.combined-swatches-item--clone');
    });

    const input = inputs.find((candidate) => {
      if (!(candidate instanceof HTMLInputElement)) return false;
      return (
        (variantId && candidate.dataset.variantId === variantId) ||
        (mediaIdString && candidate.dataset.optionMediaId === mediaIdString)
      );
    });

    if (!(input instanceof HTMLInputElement)) return false;

    input.dataset.forcedGlazeSync = 'true';
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    if (typeof productCard.selectCombinedSwatch === 'function') {
      productCard.selectCombinedSwatch(input);
    }

    delete input.dataset.forcedGlazeSync;
    return true;
  }

  /**
   * Normalizes option text for matching across labels, selects and JSON.
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeOptionValue(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    // Clay labels are inconsistent across data sources: card titles use the raw
    // option value (e.g. "Dark Grey", "Creme") while the product picker may
    // report the display-normalized value ("Dark Clay", "Creme Clay"). Map both
    // to a single canonical token so Clay matching is reliable.
    const clayAliases = {
      'dark grey': 'dark clay',
      'dark gray': 'dark clay',
      creme: 'creme clay'
    };
    return clayAliases[normalized] || normalized;
  }

  /**
   * Finds a variant that matches the selected Clay + Glaze for a product.
   * @param {string} productId - The product ID
   * @param {string} selectedGlaze - The selected glaze value
   * @param {HTMLElement} productCard - The product card element (optional, for fallback)
   * @param {string | null} [selectedClay] - The selected clay value
   * @returns {Promise<Object | null>} The matching variant data, or null if not found
   */
  async #findVariantByGlaze(productId, selectedGlaze, productCard = null, selectedClay = null) {
    // First, try to get variant info from the product card's DOM if available
    if (productCard) {
      const variantInfo = this.#getVariantFromCard(productCard, selectedGlaze, selectedClay);
      if (variantInfo) return variantInfo;
      return null;
    }

    // Fallback: fetch the product JSON to get variant information
    try {
      // Try using product handle from the card link if available
      let productUrl = `/products/${productId}.js`;
      if (productCard) {
        const cardLink = productCard.querySelector('a[href*="/products/"]');
        if (cardLink) {
          const href = cardLink.getAttribute('href');
          const match = href.match(/\/products\/([^/?]+)/);
          if (match) {
            productUrl = `/products/${match[1]}.js`;
          }
        }
      }

      const response = await fetch(productUrl);
      if (!response.ok) return null;

      const product = await response.json();
      if (!product.variants || product.variants.length === 0) return null;

      // Find the Clay and Glaze option positions
      // product.options can be an array of strings or objects with a 'name' property
      let glazeIndex = null;
      let clayIndex = null;
      if (product.options && Array.isArray(product.options)) {
        for (let i = 0; i < product.options.length; i++) {
          const option = product.options[i];
          // Handle both string and object formats
          let optionName = '';
          if (typeof option === 'string') {
            optionName = option;
          } else if (option && typeof option === 'object') {
            optionName = option.name || option.title || '';
          }
          
          if (optionName && optionName.toLowerCase() === 'glaze') {
            glazeIndex = i;
          } else if (optionName && optionName.toLowerCase() === 'clay') {
            clayIndex = i;
          }
        }
      }

      if (glazeIndex === null) return null;

      // Find a variant that matches Clay + Glaze. If Clay is unavailable for
      // some reason, fall back to glaze-only matching.
      const selectedGlazeNormalized = this.#normalizeOptionValue(selectedGlaze);
      const selectedClayNormalized = this.#normalizeOptionValue(selectedClay);
      for (const variant of product.variants) {
        if (variant.options && Array.isArray(variant.options) && variant.options[glazeIndex]) {
          // Handle both string and object formats for variant options
          const variantGlazeValue = variant.options[glazeIndex];
          const variantGlaze = typeof variantGlazeValue === 'string' 
            ? variantGlazeValue 
            : (variantGlazeValue?.name || variantGlazeValue?.value || '');

          const variantClayValue = clayIndex !== null ? variant.options[clayIndex] : '';
          const variantClay = typeof variantClayValue === 'string'
            ? variantClayValue
            : (variantClayValue?.name || variantClayValue?.value || '');

          const glazeMatches = this.#normalizeOptionValue(variantGlaze) === selectedGlazeNormalized;
          const clayMatches =
            !selectedClayNormalized ||
            clayIndex === null ||
            this.#normalizeOptionValue(variantClay) === selectedClayNormalized;

          if (glazeMatches && clayMatches) {
            return {
              id: variant.id,
              featured_media: variant.featured_media,
              image: variant.featured_image || variant.image
            };
          }
        }
      }
    } catch (error) {
      console.warn('Error fetching product data for glaze matching:', error);
    }

    return null;
  }

  /**
   * Gets variant info from the product card's DOM (if it has a variant picker)
   * @param {HTMLElement} productCard - The product card element
   * @param {string} selectedGlaze - The selected glaze value
   * @param {string | null} [selectedClay] - The selected clay value
   * @returns {Object | null} The variant info, or null if not found
   */
  #getVariantFromCard(productCard, selectedGlaze, selectedClay = null) {
    // Product cards use <swatches-variant-picker-component>, which renders the
    // combined swatch labels directly. Read those labels, skipping the carousel
    // clones (which are disabled duplicates inserted for the infinite loop).
    const labels = Array.from(productCard.querySelectorAll('label.combined-swatch')).filter(
      (label) => label instanceof HTMLElement && !label.closest('.combined-swatches-item--clone')
    );
    if (!labels.length) return null;

    const selectedGlazeNormalized = this.#normalizeOptionValue(selectedGlaze);
    const selectedClayNormalized = this.#normalizeOptionValue(selectedClay);

    const matchingLabel = labels.find((label) => {
      if (!(label instanceof HTMLElement)) return false;
      const title = label.getAttribute('title') || '';
      const ariaLabel = label.getAttribute('aria-label') || '';
      const [titleClay, titleGlaze] = title.split('|').map((value) => value.trim());
      const [ariaClay, ariaGlaze] = ariaLabel.split(' with ').map((value) => value.trim());
      const clay = titleClay || ariaClay || '';
      const glaze = titleGlaze || ariaGlaze || '';

      const glazeMatches = this.#normalizeOptionValue(glaze) === selectedGlazeNormalized;
      const clayMatches =
        !selectedClayNormalized ||
        this.#normalizeOptionValue(clay) === selectedClayNormalized;

      return glazeMatches && clayMatches;
    });

    if (matchingLabel instanceof HTMLElement) {
      const input = matchingLabel.querySelector('.combined-swatch-input[data-variant-id]');
      const variantId = input?.dataset.variantId;
      const mediaId = matchingLabel.dataset.mediaId || input?.dataset.optionMediaId;
      if (variantId) {
        return {
          id: variantId,
          mediaId: mediaId,
          glaze: selectedGlaze,
          clay: selectedClay
        };
      }
    }

    return null;
  }

  /**
   * Updates the card gallery to show a specific variant's image
   * @param {HTMLElement} cardGallery - The card gallery element
   * @param {Object} variant - The variant data
   */
  #updateCardGalleryImage(cardGallery, variant) {
    // Find the slideshow component
    const slideshow = cardGallery.querySelector('slideshow-component');
    if (!slideshow) return;

    // Get the variant's featured media ID (prioritize mediaId from DOM if available)
    const variantMediaId = variant.mediaId || variant.featured_media?.id || variant.image?.id;
    if (!variantMediaId) return;

    // Find the slide with the matching media ID
    const variantSlides = slideshow.querySelectorAll(`slideshow-slide[slide-id="${variantMediaId}"]`);
    if (variantSlides.length === 0) {
      // If we can't find by media ID, try to find by variant-image attribute
      // and check if any slide's image src matches
      const allVariantSlides = slideshow.querySelectorAll('slideshow-slide[variant-image]');
      for (const slide of allVariantSlides) {
        const img = slide.querySelector('img');
        if (img && variant.image && img.src.includes(variant.image.id)) {
          // Show this slide
          allVariantSlides.forEach((s) => { s.hidden = true; });
          slide.hidden = false;
          
          const slideId = slide.getAttribute('slide-id');
          if (slideId && slideshow.select) {
            slideshow.select({ id: slideId }, undefined, { animate: false });
          }
          return;
        }
      }
      return;
    }

    // Hide all variant image slides
    const allVariantSlides = slideshow.querySelectorAll('slideshow-slide[variant-image]');
    allVariantSlides.forEach((slide) => {
      slide.hidden = true;
    });

    // Show the matching slide
    const matchingSlide = variantSlides[0];
    if (matchingSlide) {
      matchingSlide.hidden = false;
      
      // Get the slide ID and select it in the slideshow
      const slideId = matchingSlide.getAttribute('slide-id');
      if (slideId && slideshow.select) {
        slideshow.select({ id: slideId }, undefined, { animate: false });
      }
    }
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // Ignore aborted requests - they're intentional
          if (result.aborted) {
            return;
          }
          
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          
          // Apply selected options if they exist
          const selectedGlaze = this.dataset.selectedGlaze;
          const selectedClay = this.dataset.selectedClay || null;
          if (selectedGlaze) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
              this.#updateProductCardImages(selectedGlaze, selectedClay);
            }, 0);
          }
        } else {
          // Don't hide the section if recommendations were already loaded server-side
          // Only hide if this was a client-side fetch that failed
          const wasServerSideLoaded = this.dataset.recommendationsPerformed === 'true';
          if (!wasServerSideLoaded) {
            this.#handleError(new Error('No recommendations available'));
          }
        }
      })
      .catch((e) => {
        // Ignore abort errors - they're intentional when a new request is made
        if (e.name === 'AbortError') {
          return;
        }
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } catch (error) {
      // Ignore abort errors - they're intentional when a new request is made
      if (error.name === 'AbortError') {
        return { success: false, status: 0, aborted: true };
      }
      // Re-throw other errors
      throw error;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
